import React, { useState } from 'react';
import { getCurrentPosition, formatCoordinates, isLocationTrackingEnabled } from '../firebase/location';
import './TimesheetForm.css';

const PinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const LocationStatus = ({ location, label }) => {
  if (!location) return null;
  return (
    <div className="ts-location-badge">
      <PinIcon />
      <span>{label}: {formatCoordinates(location.lat, location.lng)}</span>
    </div>
  );
};

const TimesheetForm = ({ jobId, onSubmit, onComplete, existing }) => {
  const [clockIn, setClockIn] = useState(existing?.clockIn ? new Date(existing.clockIn).toISOString().slice(0, 16) : '');
  const [clockOut, setClockOut] = useState(existing?.clockOut ? new Date(existing.clockOut).toISOString().slice(0, 16) : '');
  const [breakMinutes, setBreakMinutes] = useState(existing?.breakMinutes || 0);
  const [notes, setNotes] = useState(existing?.notes || '');
  const [submitting, setSubmitting] = useState(false);
  
  // Location tracking state
  const [clockInLocation, setClockInLocation] = useState(existing?.clockInLocation || null);
  const [clockOutLocation, setClockOutLocation] = useState(existing?.clockOutLocation || null);
  const [locationEnabled, setLocationEnabled] = useState(false);

  // Check if location tracking is enabled for this company
  React.useEffect(() => {
    isLocationTrackingEnabled().then(setLocationEnabled);
  }, []);

  const captureLocation = async () => {
    if (!locationEnabled) return null;
    const pos = await getCurrentPosition();
    return pos;
  };

  const handleClockInNow = async () => {
    setClockIn(new Date().toISOString().slice(0, 16));
    if (locationEnabled) {
      const loc = await captureLocation();
      if (loc) setClockInLocation(loc);
    }
  };

  const handleClockOutNow = async () => {
    setClockOut(new Date().toISOString().slice(0, 16));
    if (locationEnabled) {
      const loc = await captureLocation();
      if (loc) setClockOutLocation(loc);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clockIn || !clockOut) return;
    setSubmitting(true);
    const totalMinutes = Math.round((new Date(clockOut) - new Date(clockIn)) / 60000) - Number(breakMinutes);
    // Fix: if the calculation spans more than 24 hours, normalize to single-day
    const inDate = new Date(clockIn);
    const outDate = new Date(clockOut);
    let hours;
    if (totalMinutes > 24 * 60 || totalMinutes < 0) {
      const inMinutes = inDate.getHours() * 60 + inDate.getMinutes();
      const outMinutes = outDate.getHours() * 60 + outDate.getMinutes();
      let dayMinutes = outMinutes - inMinutes;
      if (dayMinutes < 0) dayMinutes += 24 * 60;
      hours = Math.max(0, (dayMinutes - Number(breakMinutes)) / 60);
    } else {
      hours = Math.max(0, totalMinutes / 60);
    }
    
    const result = await onSubmit({
      jobId,
      clockIn: new Date(clockIn).toISOString(),
      clockOut: new Date(clockOut).toISOString(),
      breakMinutes: Number(breakMinutes),
      hours: Math.round(hours * 100) / 100,
      notes,
      clockInLocation,
      clockOutLocation,
    });
    
    setSubmitting(false);
    
    if (result?.error) {
      console.error('[TimesheetForm] Submission failed:', result.error);
      return;
    }
    
    // Success — clear form
    setClockIn('');
    setClockOut('');
    setBreakMinutes(0);
    setNotes('');
    setClockInLocation(null);
    setClockOutLocation(null);
    
    if (onComplete) onComplete();
  };

  return (
    <form className="timesheet-form" onSubmit={handleSubmit}>
      <h4>{existing ? 'Update Timesheet' : 'Clock In / Out'}</h4>
      
      {locationEnabled && (
        <div className="ts-location-toggle">
          <span className="ts-location-indicator">
            <PinIcon /> GPS tracking enabled
          </span>
        </div>
      )}
      
      <div className="ts-field">
        <label>Clock In</label>
        <div className="ts-input-row">
          <input type="datetime-local" value={clockIn} onChange={(e) => setClockIn(e.target.value)} required />
          <button type="button" className="ts-now-btn" onClick={handleClockInNow}>
            {clockInLocation ? '📍 Now' : 'Now'}
          </button>
        </div>
        <LocationStatus location={clockInLocation} label="In" />
      </div>
      
      <div className="ts-field">
        <label>Clock Out</label>
        <div className="ts-input-row">
          <input type="datetime-local" value={clockOut} onChange={(e) => setClockOut(e.target.value)} required />
          <button type="button" className="ts-now-btn" onClick={handleClockOutNow}>
            {clockOutLocation ? '📍 Now' : 'Now'}
          </button>
        </div>
        <LocationStatus location={clockOutLocation} label="Out" />
      </div>
      
      <div className="ts-field">
        <label>Break (minutes)</label>
        <input type="number" min="0" value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} />
      </div>
      
      <div className="ts-field">
        <label>Notes</label>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes..." />
      </div>
      
      <button type="submit" className="ts-submit-btn" disabled={submitting}>
        {submitting ? 'Saving...' : existing ? 'Update' : 'Submit Timesheet'}
      </button>
    </form>
  );
};

export default TimesheetForm;
